import { useIntl } from 'react-intl';

import { GetBlogArticlesQuery } from '@notino/shared/definitions/types';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';

import { blogMessages } from './messages';

const DIOR_FOREVER =
  'https://cdn.notinoimg.com/images/gallery/pd/dior-articles/dior-forever-inspiration.jpg';
const DIOR_HOMME =
  'https://cdn.notinoimg.com/images/gallery/pd/dior-articles/dior-homme-inspiration.jpg';
const DIOR_JADORE =
  'https://cdn.notinoimg.com/images/gallery/pd/dior-articles/dior-jadore-inspiration.jpg';
const MISS_DIOR_INSPIRATION_SE =
  'https://cdn.notinoimg.com/images/gallery/pd/dior-articles/miss-dior-inspiration-no_for-notino.se.jpg';
const MISS_DIOR_INSPIRATION =
  'https://cdn.notinoimg.com/images/gallery/pd/dior-articles/miss-dior-inspiration.jpg';

const JADORE_NAV_VALUE = 2578;
const MISS_DIOR_NAV_VALUE = 19961;
const DIOR_HOMME_NAV_VALUE = 20139;
const DIOR_FOREVER_NAV_VALUE = 63556;

type Article = GetBlogArticlesQuery['getArticles']['articles'][number];

export const useDiorArticles = (): Article[] | null => {
  const { formatMessage } = useIntl();
  const { shopId } = useApolloClientHeaders();
  const { killswitch_pd_dior_articles } = useFeatureFlags();
  const {
    currentVariant: { navigationValues },
  } = useProductDetailContext();

  if (!killswitch_pd_dior_articles) {
    return null;
  }

  const missDiorInspirationArticle: Article = {
    title: formatMessage(blogMessages.missDiorText),
    url: '/dior-miss-dior/',
    hreflangId: 'miss-dior-article',
    image: {
      url:
        shopId === 'notino.se'
          ? MISS_DIOR_INSPIRATION_SE
          : MISS_DIOR_INSPIRATION,
      alt: 'Dior',
    },
  };
  const diorForeverArticle: Article = {
    title: formatMessage(blogMessages.diorForeverText),
    url: '/dior-forever/',
    hreflangId: 'dior-forever-article',
    image: {
      url: DIOR_FOREVER,
      alt: 'Dior',
    },
  };
  const diorHommeArticle: Article = {
    title: formatMessage(blogMessages.diorHommeText),
    url: '/dior-homme/',
    hreflangId: 'dior-homme-article',
    image: {
      url: DIOR_HOMME,
      alt: 'Dior',
    },
  };
  const diorJadoreArticle: Article = {
    title: formatMessage(blogMessages.diorJadoreText),
    url: '/dior-jadore/',
    hreflangId: 'dior-jadore-article',
    image: {
      url: DIOR_JADORE,
      alt: 'Dior',
    },
  };

  const articlesMap: Record<number, Article[]> = {
    [MISS_DIOR_NAV_VALUE]: [
      missDiorInspirationArticle,
      diorForeverArticle,
      diorHommeArticle,
    ],
    [JADORE_NAV_VALUE]: [
      diorJadoreArticle,
      diorForeverArticle,
      diorHommeArticle,
    ],
    [DIOR_HOMME_NAV_VALUE]: [
      diorHommeArticle,
      missDiorInspirationArticle,
      diorForeverArticle,
    ],
    [DIOR_FOREVER_NAV_VALUE]: [
      diorForeverArticle,
      missDiorInspirationArticle,
      diorHommeArticle,
    ],
  };

  if (navigationValues.includes(JADORE_NAV_VALUE)) {
    return articlesMap[JADORE_NAV_VALUE];
  }
  if (navigationValues.includes(MISS_DIOR_NAV_VALUE)) {
    return articlesMap[MISS_DIOR_NAV_VALUE];
  }
  if (navigationValues.includes(DIOR_HOMME_NAV_VALUE)) {
    return articlesMap[DIOR_HOMME_NAV_VALUE];
  }
  if (navigationValues.includes(DIOR_FOREVER_NAV_VALUE)) {
    return articlesMap[DIOR_FOREVER_NAV_VALUE];
  }

  return null;
};
